import type { Tenant } from '@/payload-types'
import {
  isPathOrChildPath,
  normalizePathname,
  PATIENT_PATHS,
  SHARED_PATHS,
} from '@/utilities/paths'

/**
 * Only pages are the Patient Portal pages that a tenant can expose without a full Patient Portal.
 * When any registered page is set to `only` for a tenant, that tenant is restricted: every user
 * can open the registered pages and the paths in OPEN_PATHS, and every other path is not found.
 *
 * To add a page, add one entry to ONLY_PAGE_DEFINITIONS. The entry feeds the proxy gate, the
 * public-before-login list, and the onboarding and HIPAA allowlists in `config.ts`.
 * Other sources (a feature flag, an API, another static object) can change the mode of a
 * registered page through the `overrides` argument. They cannot add a path.
 */

export type OnlyPageMode = 'disabled' | 'enabled' | 'only'

export type OnlyPageTenant = Pick<Tenant, 'addressCorrectionEnabled'>

export type OnlyPageDefinition = {
  id: string
  paths: readonly string[]
  readMode: (tenant: OnlyPageTenant | null | undefined) => OnlyPageMode
}

export type OnlyPageOverrides = Readonly<Partial<Record<string, OnlyPageMode>>>

export type OnlyPagePolicy = {
  restricted: boolean
  modes: Readonly<Record<string, OnlyPageMode>>
  definitions: readonly OnlyPageDefinition[]
}

export function normalizeOnlyPageMode(value: unknown): OnlyPageMode {
  return value === 'enabled' || value === 'only' ? value : 'disabled'
}

export const ONLY_PAGE_DEFINITIONS: readonly OnlyPageDefinition[] = [
  {
    id: 'fix-data',
    paths: [PATIENT_PATHS.fixData],
    readMode: (tenant) => normalizeOnlyPageMode(tenant?.addressCorrectionEnabled),
  },
]

export const ONLY_PAGE_PATHS: readonly string[] = ONLY_PAGE_DEFINITIONS.flatMap(
  (definition) => definition.paths,
)

// Reachable on a restricted tenant even though they are not Only pages. Logout lets a signed-in
// person leave, the Google callback lets a started sign-in finish, and Payload and the health
// check are not part of the Patient Portal. /404 stays open so the proxy can rewrite it to
// /not-found, which renders the app 404 screen. /not-found stays open so that rewrite is not closed.
const OPEN_PATHS: readonly string[] = [
  SHARED_PATHS.logout,
  SHARED_PATHS.oauthGooglePopupCallback,
  SHARED_PATHS.admin,
  SHARED_PATHS.healthy,
  SHARED_PATHS.notFound,
  SHARED_PATHS.notFoundRender,
]

export function isAlwaysOpenPath(pathname: string): boolean {
  const path = normalizePathname(pathname)
  return OPEN_PATHS.some((openPath) => isPathOrChildPath(path, openPath))
}

export function resolveOnlyPolicy(args: {
  tenant: OnlyPageTenant | null | undefined
  overrides?: OnlyPageOverrides
  definitions?: readonly OnlyPageDefinition[]
}): OnlyPagePolicy {
  const definitions = args.definitions ?? ONLY_PAGE_DEFINITIONS
  const modes: Record<string, OnlyPageMode> = {}

  for (const definition of definitions) {
    const override = args.overrides?.[definition.id]
    modes[definition.id] = override ?? definition.readMode(args.tenant)
  }

  return {
    restricted: Object.values(modes).includes('only'),
    modes,
    definitions,
  }
}

export function canAccessPath(pathname: string, policy: OnlyPagePolicy): boolean {
  const path = normalizePathname(pathname)

  const page = policy.definitions.find((definition) =>
    definition.paths.some((registeredPath) => isPathOrChildPath(path, registeredPath)),
  )
  if (page) {
    return policy.modes[page.id] !== 'disabled'
  }

  return !policy.restricted || isAlwaysOpenPath(path)
}

export function isPathAvailableForTenant(
  tenant: OnlyPageTenant | null | undefined,
  pathname: string,
): boolean {
  return canAccessPath(pathname, resolveOnlyPolicy({ tenant }))
}

export function isTenantRestricted(tenant: OnlyPageTenant | null | undefined): boolean {
  return resolveOnlyPolicy({ tenant }).restricted
}
